import React, { useState } from 'react'
import moment from 'moment';
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import { SelectValidator, ValidatorForm } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';
import { getPartner } from '../../../../../lib/access.es6';

// Benefits that are NOT independently claimable in Step 2. Grocery Allowance and
// Bus Cash Benefit are add-ons: the agent picks a primary funeral benefit and we
// auto-attach these (see BENEFIT_BUNDLES) behind the scenes when present on the
// policy, so they must not appear as selectable primary benefits here.
const NON_SELECTABLE_BENEFITS = [
  'Rewards',
  'Cashback',
  'Grocery Allowance',
  'Bus Cash Benefit',
];

// Add-on benefits bundled with each selectable primary benefit. Only the add-ons
// that actually exist on the member's policy are attached. Hospital Cash Benefit
// is standalone (no bundle).
const BENEFIT_BUNDLES = {
  'Funeral Services': ['Grocery Allowance', 'Bus Cash Benefit'],
  'Funeral Service': ['Grocery Allowance', 'Bus Cash Benefit'],
  'Cash in Lieu of Service': ['Bus Cash Benefit'],
  'Muslim Funeral': ['Bus Cash Benefit'],
};

// Mutually-exclusive death/funeral benefits. A member dies once, so once ANY of
// these has been utilised for a member, none of them can be claimed again — we
// disable the whole family in the dropdown.
const FUNERAL_FAMILY_BENEFITS = [
  'Funeral Services',
  'Funeral Service',
  'Cash in Lieu of Service',
  'Muslim Funeral',
];

// A benefit counts as already utilised once its claim reaches one of these
// statuses (approved, or approved and paid out).
const UTILISED_CLAIM_STATUSES = ['Approved', 'Approved_Paid'];

// Collect the set of benefit types already utilised by a specific member
// (dependant) on the selected policy, looking only at approved / approved-paid
// claims. Returns a plain lookup object keyed by benefit type.
const getUtilisedBenefitTypes = (claims, policyGuid, dependantGuid) => {
  const utilised = {};
  if (!policyGuid || !dependantGuid) {
    return utilised;
  }
  (claims || []).forEach(function (claim) {
    if (!claim) return;
    if (claim.policy_guid !== policyGuid) return;
    if (claim.dependant_guid !== dependantGuid) return;
    if (UTILISED_CLAIM_STATUSES.indexOf(claim.status) === -1) return;

    (claim.benefit_usages || []).forEach(function (usage) {
      if (usage && usage.benefit_type) {
        utilised[usage.benefit_type] = true;
      }
    });
    (claim.benefits || []).forEach(function (benefit) {
      if (benefit && benefit.type) {
        utilised[benefit.type] = true;
      }
    });
  });
  return utilised;
};
// Every benefit on the quote carries its own waiting_period_in_days measured from its own
// waiting_period_start_date (not necessarily the policy's start_date - e.g. a benefit added via a later
// amendment gets its own, later start date), so a claim shouldn't be submittable at all until that's
// elapsed. This is the same rule the backend itself enforces at submission time ("The waiting period for
// this covered member has not yet elapsed...") - checking it here just avoids sending an agent through the
// whole claim form only to have it rejected at the very end.
function daysSinceWaitingPeriodStart(benefit) {
  if (!benefit || !benefit.waiting_period_start_date) return null;
  return moment().diff(moment(benefit.waiting_period_start_date), 'days');
}

function isBenefitWaitingPeriodMet(benefit) {
  // Hospital Cash Benefit is excluded here - unlike every other benefit type, its real waiting period
  // is per-CAUSE (illness/surgery/accident/pregnancy) and anchored on the policy's start_date,
  // not this benefit record's own waiting_period_start_date (see HospitalClaimDetails.js, which gates
  // it correctly once a cause is selected). Applying this generic, wrongly-anchored check here too
  // would show a misleading eligibility signal before that point.
  if (benefit.type === 'Hospital Cash Benefit') return true;
  // Funeral-family (death) benefits are excluded here for the same reason: per the signed BRD
  // (§4.3.3), the waiting period is waived entirely when cause of death is Accidental - and cause
  // of death isn't known yet at this step, it's captured later in FuneralClaimDetails.js, which
  // gates the cause-of-death dropdown itself (disabling Natural/Suicide, never Accidental) once
  // the real per-cause determination can be made. Disabling the benefit here regardless of cause
  // would block agents from even starting an accidental-death claim that should be immediately
  // claimable.
  if (FUNERAL_FAMILY_BENEFITS.indexOf(benefit.type) !== -1) return true;
  const daysSince = daysSinceWaitingPeriodStart(benefit);
  if (daysSince === null || benefit.waiting_period_in_days == null) return true;
  return daysSince >= benefit.waiting_period_in_days;
}

function benefitEligibleFromDate(benefit) {
  return moment(benefit.waiting_period_start_date)
    .add(benefit.waiting_period_in_days, 'days')
    .format('DD-MM-YYYY');
}

const SelectMember = (props) => {

  console.log("select member props", props)
  if (!props.eventQuote) {
    return
  }

  const classes = useStyle();
  const [selectMember, setSelectMember] = useState('');
  const [selectBenefit, setSelectBenefit] = useState('');


  const handleChange = (event) => {
    const { name, value } = event.target

    if (name === "selectmember") {
      setSelectMember(value)
      console.log("member details", value)
    }

  }


  const handleMember = (benefits) => {
    const tempClaimInitiation = props.claimInitiationPayload;
    
    // Map selected benefit types to their full benefit objects
    const selectedBenefitObjects = benefits.map((benefitType) => {
      const fullBenefit = selectMember.benefits.find(b => b.type === benefitType);
      return fullBenefit || { type: benefitType };
    });
    
    tempClaimInitiation["benefits"] = selectedBenefitObjects;
    console.log("temp claim init", { tempClaimInitiation })
    tempClaimInitiation["dependant_guid"] = selectMember.dependant.guid;
    tempClaimInitiation["member"] = selectMember
    props.setClaimInitiationPayload(tempClaimInitiation);
  }


  // The agent selects one primary benefit; we auto-attach its bundled add-ons
  // (Grocery / Bus Cash) when those benefits exist on the policy.
  const handleBenefitChange = (event) => {
    const { value } = event.target;
    setSelectBenefit(value);

    if (!value) {
      handleMember([]);
      return;
    }

    const availableTypes = (selectMember.benefits || []).map(function (b) {
      return b.type;
    });
    const bundled = (BENEFIT_BUNDLES[value] || []).filter(function (type) {
      return availableTypes.indexOf(type) !== -1;
    });

    handleMember([value].concat(bundled));
    console.log('Selected benefit:', value, 'auto-attached:', bundled);
  };
  const members = props.eventQuote.policy_schedule_entries ? props.eventQuote.policy_schedule_entries : []
  const currencyCode = getPartner().country.currency_code

  // Work out which benefits this member has already utilised on the selected
  // policy, so we can disable them in the dropdown below. Funeral-family
  // benefits are mutually exclusive, so if any of them has been utilised we
  // disable all of them.
  const selectedDependantGuid =
    selectMember && selectMember.dependant ? selectMember.dependant.guid : null
  const utilisedBenefitTypes = getUtilisedBenefitTypes(
    props.customerClaimsDcp,
    props.eventQuote.policy_guid,
    selectedDependantGuid
  )
  const funeralFamilyUsed = FUNERAL_FAMILY_BENEFITS.some(function (type) {
    return !!utilisedBenefitTypes[type]
  })

  console.log("selected member", selectMember)


  return (
    <>

      <ValidatorForm
        onSubmit={() => { }}
      >
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} md={6} sm={6}>
            <SelectValidator
              labelId="select-member"
              id="select-member"
              value={selectMember}
              name='selectmember'
              type="text"
              label="Select Member"
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select a member']}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >

              {
                members.map((entry, index) => (
                  <MenuItem
                    key={index}
                    sx={{
                      fontFamily: "Open Sans",
                      fontStyle: "normal",
                      fontWeight: 300,
                      fontSize: "12px",
                      lineHeight: "16px",
                      letterSpacing: "0.005em",
                      color: "#000000",
                    }}
                    value={entry}>{entry.dependant.first_name + " " + entry.dependant.last_name}</MenuItem>
                ))
              }

            </SelectValidator>
          </Grid>
          <Grid item xs={12} md={6} sm={6}>
            <SelectValidator
              labelId="select-benefit"
              id="select-benefit"
              value={selectBenefit}
              name='selectbenefit'
              type="text"
              label="Select Benefit"
              onChange={handleBenefitChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select a benefit']}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >
              {
                (selectMember ? selectMember.benefits : [])
                  .filter(benefit => NON_SELECTABLE_BENEFITS.indexOf(benefit.type) === -1)
                  .map((benefit, index) => {
                    // Disable the whole funeral family once this member has
                    // already utilised any funeral-family benefit.
                    const isFuneralFamily =
                      FUNERAL_FAMILY_BENEFITS.indexOf(benefit.type) !== -1
                    const isUtilised = isFuneralFamily && funeralFamilyUsed
                     const waitingPeriodMet = isBenefitWaitingPeriodMet(benefit);
                    return (
                    <MenuItem
                      key={index}
                      disabled={isUtilised || !waitingPeriodMet}
                      value={benefit.type}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "10px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                    >
                      {benefit.type} --
                      {
                        benefit.type === "HOSPITAL ADMISSION" ? (
                          <>
                            {currencyCode + " " + ((benefit.cover_amount_in_cents / benefit.cover_usage_count) / 100).toLocaleString()} / per night
                          </>
                        ) : (
                          <>Amount: {currencyCode}  {benefit.cover_amount_in_cents / 100}</>
                        )
                      }
                      {isUtilised ? " (already claimed)" : ""}
                        {
                          !waitingPeriodMet && (
                            <> (waiting period not met, eligible from {benefitEligibleFromDate(benefit)})</>
                          )
                        }

                    </MenuItem>
                    )
                  })
              }
            </SelectValidator>
            {funeralFamilyUsed ? (
              <div
                style={{
                  marginTop: "6px",
                  fontFamily: "Open Sans",
                  fontSize: "10px",
                  color: "#b71c1c",
                }}
              >
                A funeral benefit has already been claimed for this member, so
                the funeral, cash-in-lieu and Muslim funeral options are
                unavailable.
              </div>
            ) : null}
          </Grid>
        </Grid>
      </ValidatorForm >
    </>
  )
}

export default SelectMember